import { watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useRuntimeConfig, useCookie, useRouter } from 'nuxt/app'

import { useLocationStore } from '@common/location-layer/store/location.store'

import { PushNotificationsService } from '../services/push-notifications.service'
import type { Messaging } from 'firebase/messaging'

/** FCM token waiting for `locationId` when init-location failed or lagged */
let pendingFcmToken: string | null = null
let stopPendingLocationWatch: (() => void) | null = null

/** Unsubscribe from modular `onMessage` when plugin disposes or handler is re-registered */
let stopForegroundMessageListener: (() => void) | null = null

function stopPendingRegistrationWatch(): void {
  stopPendingLocationWatch?.()
  stopPendingLocationWatch = null
}

function stopForegroundListener(): void {
  stopForegroundMessageListener?.()
  stopForegroundMessageListener = null
}

/**
 * Composable for Firebase Cloud Messaging push notifications.
 *
 * Ported from @ghl/clientportal-core with these responsibilities:
 *  - Request browser notification permission
 *  - Obtain an FCM token via Firebase Messaging v9+ SDK
 *  - Register the token with the backend
 *  - Handle foreground messages (show system notification via SW)
 *  - Bridge SW → app: listen for `redirect-from-notificationclick` messages
 *    and navigate the app (push payloads are separate from in-app notifications)
 */
export function usePushNotifications() {
  const runtimeConfig = useRuntimeConfig()
  const router = useRouter()

  /**
   * True when `runtimeConfig.public.firebaseMessaging` has non-empty `apiKey` and `appId`
   * (supplied at runtime via `NUXT_PUBLIC_FIREBASE_MESSAGING_API_KEY` /
   * `NUXT_PUBLIC_FIREBASE_MESSAGING_APP_ID`). If either is missing, FCM must not run — avoids
   * permission prompts and Firebase init errors.
   */
  function isFirebaseMessagingConfigured(): boolean {
    const { apiKey, appId } = runtimeConfig.public.firebaseMessaging
    return Boolean(String(apiKey ?? '').trim() && String(appId ?? '').trim())
  }

  // ── Firebase Messaging (lazy, singleton) ──────────────────────────────────
  let _messagingInstance: Messaging | null = null

  async function _getMessaging(): Promise<Messaging | null> {
    if (!isFirebaseMessagingConfigured()) return null
    if (_messagingInstance) return _messagingInstance

    try {
      const { initializeApp, getApps } = await import('firebase/app')
      const { getInstallations } = await import('firebase/installations')
      const { getMessaging } = await import('firebase/messaging')

      const { apiKey, authDomain, projectId, messagingSenderId, appId } = runtimeConfig.public.firebaseMessaging

      const firebaseApp = getApps().length ? getApps()[0] : initializeApp({ apiKey, authDomain, projectId, messagingSenderId, appId })

      // Ensure Installations gets registered before Messaging asks for
      // `installations-internal`; this avoids runtime errors in some optimized bundles.
      getInstallations(firebaseApp)
      _messagingInstance = getMessaging(firebaseApp)
      return _messagingInstance
    } catch (err) {
      console.error('[FCM] Failed to initialise Firebase Messaging:', err)
      return null
    }
  }

  function _firebaseMessagingSwRegistration(registrations: readonly ServiceWorkerRegistration[]): ServiceWorkerRegistration | undefined {
    return registrations.find(r => r.active?.scriptURL?.includes('firebase-messaging-sw.js'))
  }

  function ensurePendingRegistrationWatch(): void {
    if (stopPendingLocationWatch) return
    const locationStore = useLocationStore()
    const { locationId } = storeToRefs(locationStore)
    stopPendingLocationWatch = watch(
      locationId,
      async id => {
        if (!String(id ?? '').trim() || !pendingFcmToken) return
        const token = pendingFcmToken
        pendingFcmToken = null
        await _registerPushToken(token)
      },
      { flush: 'post' }
    )
  }

  // ── Token → backend ───────────────────────────────────────────────────────
  async function _registerPushToken(token: string): Promise<void> {
    try {
      const locationStore = useLocationStore()
      const locationId = locationStore.locationId

      if (!String(locationId ?? '').trim()) {
        console.warn('[FCM] locationId not available — deferring push token registration until location is ready')
        pendingFcmToken = token
        ensurePendingRegistrationWatch()
        return
      }

      // Use the session cookie when available; fall back to a generated UUID
      const sessionCookie = useCookie('sessionId')
      const sessionId = sessionCookie.value ?? crypto.randomUUID()

      const service = new PushNotificationsService()
      await service.registerPushToken({ pushToken: token, sessionId, deviceType: 'web', appType: 'ClientPortal' }, locationId)

      pendingFcmToken = null
      stopPendingRegistrationWatch()
    } catch (err) {
      console.error('[FCM] Push token registration failed:', err)
    }
  }

  // ── Permission + token registration ───────────────────────────────────────
  /**
   * Request notification permission, obtain the FCM token and register it
   * with the backend.  Skips silently on WebKit (Safari) which does not
   * support the Web Push standard.
   */
  async function requestAndRegisterPushToken(): Promise<void> {
    if (!isFirebaseMessagingConfigured()) return

    // Safari / WebKit does not support Web Push — skip silently
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return

    if (!('Notification' in window)) return
    if (!('serviceWorker' in navigator)) return

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return

    const messaging = await _getMessaging()
    if (!messaging) return

    try {
      const { getToken } = await import('firebase/messaging')

      // Prefer the firebase-messaging-sw.js registration we serve via Nitro
      const registrations = await navigator.serviceWorker.getRegistrations()
      const swReg = _firebaseMessagingSwRegistration(registrations)

      // Pass vapidKey only when configured (staging has one; production uses Firebase's
      // auto-generated default key — matching the old clientportal-core behaviour).
      const vapidKey = runtimeConfig.public.firebaseMessaging.webPushCertificate || undefined

      const token = await getToken(messaging, {
        ...(vapidKey ? { vapidKey } : {}),
        serviceWorkerRegistration: swReg,
      })

      if (token) {
        await _registerPushToken(token)
      }
    } catch (err) {
      console.error('[FCM] Failed to obtain FCM token:', err)
    }
  }

  // ── Foreground message handler (app is open) ───────────────────────────────
  /**
   * When the app is in the foreground Firebase does not show a system
   * notification automatically.  We display it manually via the active SW
   * so behaviour is consistent with background messages.
   */
  async function setupForegroundMessageHandler(): Promise<void> {
    if (!isFirebaseMessagingConfigured()) return

    const messaging = await _getMessaging()
    if (!messaging) return

    stopForegroundListener()

    const { onMessage } = await import('firebase/messaging')

    stopForegroundMessageListener = onMessage(messaging, async payload => {
      const title = payload.notification?.title ?? ''
      const options: NotificationOptions = {
        body: payload.notification?.body,
        data: payload.data,
      }

      const registrations = await navigator.serviceWorker.getRegistrations()
      const swReg = _firebaseMessagingSwRegistration(registrations)
      if (swReg) {
        await swReg.showNotification(title, options)
      }
    })
  }

  // ── SW → app bridge (notification click → in-app route) ────────────────────
  /**
   * Registers a listener for `redirect-from-notificationclick` messages posted
   * by the firebase-messaging-sw.js service worker and navigates the SPA.
   *
   * Returns a cleanup function that removes the event listener — call it
   * from plugin teardown if needed.
   */
  function setupSWMessageListener(): () => void {
    if (!isFirebaseMessagingConfigured()) return () => {}
    if (!('serviceWorker' in navigator)) return () => {}

    async function onSWMessage(event: MessageEvent): Promise<void> {
      if (event.data?.action !== 'redirect-from-notificationclick') return

      const raw = event.data?.redirectPath
      const path = typeof raw === 'string' ? raw.trim() : ''
      if (!path) {
        if (raw != null && raw !== '') console.warn('[FCM] Ignoring redirectPath — expected non-empty string:', raw)
        return
      }

      // Intentionally `router.push` (not CPNavigateTo): the SW forwards `redirectPath` from
      // FCM payload data — legacy clientportal-core contract. Route names vary across shells;
      // CPNavigateTo would need an allowlist to resolve these paths.
      const target = path.startsWith('/') ? path : `/${path}`
      await router.push(target)
    }

    navigator.serviceWorker.addEventListener('message', onSWMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', onSWMessage)
    }
  }

  /** Stops deferred `locationId` watch and foreground `onMessage` listener (e.g. plugin HMR / teardown). */
  function disposePushNotificationSideEffects(): void {
    stopPendingRegistrationWatch()
    stopForegroundListener()
  }

  return {
    isFirebaseMessagingConfigured,
    requestAndRegisterPushToken,
    setupForegroundMessageHandler,
    setupSWMessageListener,
    disposePushNotificationSideEffects,
  }
}
